/* eslint-disable max-classes-per-file */
import { isNil } from "lodash";
import { Cellstyle } from "~app/common/components/hot-doc/hotdoc/cell";
import { ObjectUtils } from "~app/common/utils/object-utils";
import * as Request from "../../dto/vardeberakning/vardeberakning-request-dto";
import * as Response from "../../dto/virkeskostnader/virkeskostnader-dto";
import { VKDirektutbyteProduktgruppDTO } from "../tradslag/direktutbyte-models";
import { VKSnabbkalkylProduktGruppDTO } from "../tradslag/snabbkalkyl-models";

type CellNameSnabbkalkyl = keyof Omit<Virkeskostnad, "guid">;
export type VKVirkeskostnaderHDCellStyles =  Partial<Record<CellNameSnabbkalkyl, Cellstyle>>;

export type VKProduktGrupp = VKSnabbkalkylProduktGruppDTO | VKDirektutbyteProduktgruppDTO;

export interface IVirkeskostnadUtbyte {
    utbyteInlastTid?: Date;
    utbyteInlastFran?: string;
    utbyteInlastAreal?: number;
    utbyte: VKProduktGrupp[];
}

export interface IVirkeskostnadAtgard {
    atgardId: number;
    atgardGuid: string;
    nettoareal: number;
    utbyteInlastTid?: Date;
    utbyteInlastFran?: string;
    utbyteInlastAreal?: number;
}

export class VirkeskostnadUtbyte implements IVirkeskostnadUtbyte {
    utbyteInlastTid?: Date;
    utbyteInlastFran?: string;
    utbyteInlastAreal?: number;
    utbyte: VKProduktGrupp[];
}

export class Virkeskostnader {
    kontraktId: number;
    kontraktGUID: string;
    kontraktVirkeskostnader: Virkeskostnad[];
    atgarderVirkeskostnader: AtgardVirkeskostnader[];
    source?: any;

    constructor(dto?: Response.VKVirkeskostnaderResponseDTO) {
        if (!isNil(dto)) {
            this.kontraktId = dto.kontraktId;
            this.kontraktGUID = dto.kontraktGUID;
            this.atgarderVirkeskostnader = dto.atgarderVirkeskostnader?.map(k => new AtgardVirkeskostnader(k));
            this.kontraktVirkeskostnader = dto.kontraktVirkeskostnader?.map(k => new Virkeskostnad(k));
        } else {
            this.atgarderVirkeskostnader = [];
            this.kontraktVirkeskostnader = [];
        }
    }

    getOcertifieratHandelssortimentNamn(): string {
        const kontraktNamn = this.kontraktVirkeskostnader.find(x => x.ocertifieratHandelssortiment)?.handelssortimentNamn;
        if (kontraktNamn) {
            return kontraktNamn;
        }

        const atgarderNamn = this.atgarderVirkeskostnader
            .find(x => x.virkeskostnader.some(v => v.ocertifieratHandelssortiment))
            ?.virkeskostnader.find(v => v.ocertifieratHandelssortiment)?.handelssortimentNamn;

        return atgarderNamn || null;
    }

    hasHandelssortiment(): boolean {
        if(ObjectUtils.isNullOrUndefined(this.kontraktVirkeskostnader)
            && ObjectUtils.isNullOrUndefined(this.atgarderVirkeskostnader)
        ) {
            return false;
        }

        return this.kontraktVirkeskostnader.length > 0
            || this.atgarderVirkeskostnader.some(x =>
                x.virkeskostnader?.length > 0);
    }

    getOrCreateVirkeskostnadForKontrakt(guid: string): Virkeskostnad {
        let k = this.kontraktVirkeskostnader.find(x => x.guid === guid);
        if (isNil(k)) {
            k = new Virkeskostnad();
            k.guid = guid;
            this.kontraktVirkeskostnader.push(k);
        }
        return k;
    }

    getForAtgard(atgardGuid: string): AtgardVirkeskostnader {
        return this.atgarderVirkeskostnader.find(x => x.virkeskopsatgardGUID === atgardGuid);
    }

    getForAllaAtgarder(): Virkeskostnad[] {
        return this.atgarderVirkeskostnader.reduce((a,b) => a.concat(b.virkeskostnader), []);
    }

    getOrCreateVirkeskostnaderForAtgard(atgardGuid: string): AtgardVirkeskostnader {
        let atg = this.getForAtgard(atgardGuid);
        if (isNil(atg)) {
            atg = new AtgardVirkeskostnader();
            atg.virkeskopsatgardGUID = atgardGuid;
            atg.virkeskopsatgardId = null;
            atg.virkeskostnader = [];
            this.atgarderVirkeskostnader.push(atg);
        }
        return atg;
    }
}

export class AtgardVirkeskostnader {
    virkeskopsatgardId: number;
    virkeskopsatgardGUID: string;
    virkeskostnader: Virkeskostnad[];

    constructor(dto?: Response.VKAtgardVirkeskostnadDTO) {
        if (!isNil(dto)) {
            this.virkeskopsatgardId = dto.virkeskopsatgardId;
            this.virkeskopsatgardGUID = dto.virkeskopsatgardGUID;
            this.virkeskostnader = dto.virkeskostnader?.map(d => new Virkeskostnad(d));
        } else {
            this.virkeskostnader = [];
        }
    }

    getOrCreateVirkeskostnad(guid: string): Virkeskostnad {
        let k = this.virkeskostnader.find(x => x.guid === guid);
        if (isNil(k)) {
            k = new Virkeskostnad();
            k.guid = guid;
            this.virkeskostnader.push(k);
        }
        return k;
    }

    remove(guid: string): void {
        const idx = this.virkeskostnader.findIndex(x => x.guid === guid);
        if (idx > -1) {
            this.virkeskostnader.splice(idx, 1);
        }
    }

}

export class AtgardVirkeskostnad {
    kontraktGUID: string;
    virkeskopsatgardGUID: string;
    virkeskostnad: Virkeskostnad;

    constructor(kontraktGUID: string, atgardGUID: string, virkeskostnad: Virkeskostnad) {
        this.kontraktGUID = kontraktGUID;
        this.virkeskopsatgardGUID = atgardGUID;
        this.virkeskostnad = virkeskostnad;
    }
}

export class VirkeskostnadEvent {
    kontraktGUID: string;
    virkeskopsatgardGUID?: string;
    virkeskostnad: Virkeskostnad;
    source: any;

    constructor(kontraktGUID: string, virkeskopsatgardGUID: string, virkeskostnad: Virkeskostnad, source: any) {
        this.kontraktGUID = kontraktGUID;
        this.virkeskopsatgardGUID = virkeskopsatgardGUID;
        this.virkeskostnad = virkeskostnad;
        this.source = source;
    }

    isMatch(kontraktGUID: string , virkeskopsatgardGUID?: string) {
        return this.kontraktGUID === kontraktGUID &&
        ((isNil(this.virkeskopsatgardGUID)) ||
        (!isNil(virkeskopsatgardGUID) &&
        this.virkeskopsatgardGUID === virkeskopsatgardGUID));
    }
}

export class VirkeskostnaderEvent {
    virkeskostnader: Virkeskostnader;
    source: any;

    constructor(virkeskostnader: Virkeskostnader, source?: any) {
        this.virkeskostnader = virkeskostnader;
        this.source = source;
    }
}

export class Virkeskostnad {
    id?: number;
    guid?: string;
    grundpris?: number;
    kvantitet?: number;
    kvantitetM3fub?: number;
    mattslag: string;
    tackningsbidrag?: number;
    tillaggAvdrag?: number;
    fsc?: number;
    pefcSC?: number;
    handelssortimentId?: number;
    handelssortimentNamn: string;
    handelssortimentsgruppId: number;
    handelssortimentsgruppNamn: string;
    handelssortimentsgruppSekvensnummer?: number;
    konverteringsfaktorHandelssortimentMattslag?: number;
    ocertifieratHandelssortiment?: boolean;
    utbyteInlastKvantitet?: number;

    constructor(dto?: Response.VKVirkeskostnadDTO) {
        if (!isNil(dto)) {
            this.id = dto.id;
            this.guid = dto.guid;
            this.grundpris = dto.grundpris;
            this.kvantitet = dto.kvantitet;
            this.kvantitetM3fub = dto.kvantitet * dto.konverteringsfaktorHandelssortimentMattslag;
            this.mattslag = dto.mattslag;
            this.tackningsbidrag = dto.tackningsbidrag;
            this.tillaggAvdrag = dto.tillaggAvdrag;
            this.fsc = dto.fsc;
            this.pefcSC = dto.pefcSC;
            this.handelssortimentId = dto.handelssortimentId;
            this.handelssortimentNamn = dto.handelssortimentNamn;
            this.handelssortimentsgruppId = dto.handelssortimentsgruppId;
            this.handelssortimentsgruppNamn = dto.handelssortimentsgruppNamn;
            this.handelssortimentsgruppSekvensnummer = dto.handelssortimentsgruppSekvensnummer;
            this.konverteringsfaktorHandelssortimentMattslag = dto.konverteringsfaktorHandelssortimentMattslag;
            this.ocertifieratHandelssortiment = dto.ocertifieratHandelssortiment;
            this.utbyteInlastKvantitet = dto.utbyteInlastKvantitet;
        }
    }

    mapToDto(): Request.VirkeskostnadDTO {
        return {
            handelssortimentNamn: this.handelssortimentNamn,
            handelssortimentId: this.handelssortimentId,
            handelssortimentsgruppId: this.handelssortimentsgruppId,
            handelssortimentsgruppNamn: this.handelssortimentsgruppNamn,
            handelssortimentsgruppSekvensnummer: this.handelssortimentsgruppSekvensnummer,
            mattslag: this.mattslag,
            kvantitet: this.kvantitet,
            grundpris: this.grundpris,
            tillaggAvdrag: this.tillaggAvdrag,
            fsc: this.fsc,
            pefc: this.pefcSC,
            tackningsbidrag: this.tackningsbidrag,
            utbyteInlastKvantitet: this.utbyteInlastKvantitet
        };
    }
}

export interface IHandelssortimentVirkeskostnadOption {
    id?: number;
    checked: boolean;
    beskrivning: string;
    namn: string;
    handelssortimentsgruppNamn: string;
    handelssortimentsgruppId?: number;
    handelssortimentsgruppSekvensnummer: number;
    produkter: string[];
    foreslagetMattslag: string;
}

export class HandelssortimentVirkeskostnadOption implements IHandelssortimentVirkeskostnadOption {
    id?: number;
    checked: boolean;
    beskrivning: string;
    namn: string;
    handelssortimentsgruppNamn: string;
    handelssortimentsgruppId: number;
    handelssortimentsgruppSekvensnummer: number;
    produkter: string[];
    aktiv: boolean;
    foreslagetMattslag: string;
    constructor(
        id: number,
        checked: boolean,
        beskrivning: string,
        namn: string,
        handelssortimentsgruppNamn: string,
        handelssortimentsgruppSekvensnummer: number,
        handelssortimentsgruppId: number,
        produkter: string[],
        aktiv: boolean,
        foreslagetMattslag: string) {
        this.id = id;
        this.checked = checked;
        this.beskrivning = beskrivning;
        this.namn = namn;
        this.produkter = produkter;
        this.aktiv = aktiv;
        this.handelssortimentsgruppNamn = handelssortimentsgruppNamn;
        this.handelssortimentsgruppSekvensnummer = handelssortimentsgruppSekvensnummer;
        this.handelssortimentsgruppId = handelssortimentsgruppId;
        this.foreslagetMattslag = foreslagetMattslag;
    }
}
